import { ReactNode, useState } from 'react'

import { Skeleton } from 'antd'
import classNames from 'classnames'

import { ConditionalWrapper } from 'components/ConditionalWrapper'
import { Flex } from 'components/core'
import { ImageNotFoundIcon } from 'components/Icons'
import { AsinLink } from 'components/Links'
import ProductCatalogLink from 'components/Links/ProductCatalogLink'
import { ResearchKeywordLink } from 'components/Links/ResearchKeywordLink/ResearchKeywordLink'
import { ToolTip } from 'components/ToolTip'
import { ProductCatalogTabId } from 'const/productCatalog'
import { CountryCode } from 'types'

import * as styles from './styles.module.scss'

interface ProductTitleProps {
    value: ReactNode
    productImg?: string | null
    productAsin?: string
    productMarketplace?: string
    showImage?: boolean
    size?: 'large' | 'medium' | 'small'
    isLoading?: boolean
    numChildren?: number
    isParent?: boolean
    titleFormatter?: (value: ReactNode) => ReactNode
    parentAsinMessage?: string
    additionalLinkContent?: ReactNode
    linkToProductDetails?: boolean
    productDetailsTabToLinkTo?: ProductCatalogTabId
    onProductClick?: () => void
}

type ProductTitleSize = NonNullable<ProductTitleProps['size']>

const IMAGE_DIMENSIONS: Record<
    ProductTitleSize,
    { wrapper: number; image: number }
> = {
    large: { wrapper: 50, image: 44 },
    medium: { wrapper: 42, image: 36 },
    small: { wrapper: 26, image: 20 },
}

function ProductTitle({
    value,
    productImg,
    productAsin,
    productMarketplace,
    showImage = true,
    size = 'large',
    isLoading = false,
    numChildren = 0,
    isParent = false,
    titleFormatter,
    parentAsinMessage,
    additionalLinkContent,
    linkToProductDetails = false,
    productDetailsTabToLinkTo,
    onProductClick,
}: ProductTitleProps): ReactNode {
    const [imageOK, setImageOK] = useState(true)

    const title = titleFormatter ? titleFormatter(value) : value

    return (
        <Flex gap={8} className={styles.wrapper}>
            {isLoading && (
                <Flex gap={12} alignItems="normal">
                    {showImage && (
                        <div className={styles.loadingImageWrap}>
                            <Skeleton.Image
                                active
                                className={styles.smallerSkeletonImage}
                            />
                        </div>
                    )}
                    <Flex direction="column" alignItems="start">
                        <div className={styles.title}>
                            <Skeleton
                                active
                                title={false}
                                className={styles.compressedSkeleton}
                                paragraph={{
                                    rows: 2,
                                    width: [140, 100],
                                }}
                            />
                        </div>
                    </Flex>
                </Flex>
            )}
            {!isLoading && (
                <>
                    {showImage && (
                        <div className={styles.imageOuter}>
                            {isParent && (
                                <div
                                    className={classNames(
                                        styles.imageWrapper,
                                        styles.stackBaseImageWrapper
                                    )}
                                    style={{
                                        width: IMAGE_DIMENSIONS[size].wrapper,
                                        height: IMAGE_DIMENSIONS[size].wrapper,
                                    }}
                                />
                            )}
                            <div
                                className={styles.imageWrapper}
                                style={{
                                    width: IMAGE_DIMENSIONS[size].wrapper,
                                    height: IMAGE_DIMENSIONS[size].wrapper,
                                }}
                            >
                                {isParent && (
                                    <ConditionalWrapper
                                        condition={
                                            parentAsinMessage !== undefined
                                        }
                                        wrapper={(children) => (
                                            <ToolTip
                                                placement="top"
                                                title={parentAsinMessage}
                                            >
                                                {children}
                                            </ToolTip>
                                        )}
                                    >
                                        <div className={styles.childCount}>
                                            {numChildren}
                                        </div>
                                    </ConditionalWrapper>
                                )}
                                <div
                                    className={styles.imageContent}
                                    style={{
                                        width: IMAGE_DIMENSIONS[size].image,
                                        height: IMAGE_DIMENSIONS[size].image,
                                    }}
                                >
                                    {productImg && imageOK ? (
                                        <img
                                            src={productImg}
                                            alt={String(title)}
                                            onError={() => setImageOK(false)}
                                        />
                                    ) : (
                                        <ImageNotFoundIcon
                                            className={styles.missingImageIcon}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <Flex direction="column" alignItems="start">
                        <div
                            className={classNames(styles.title, {
                                [styles.smallTitle]: size === 'small',
                            })}
                        >
                            <span className={styles.productTitleText}>
                                {linkToProductDetails &&
                                productAsin &&
                                productMarketplace ? (
                                    <ProductCatalogLink
                                        asin={productAsin}
                                        countryCode={
                                            productMarketplace as CountryCode
                                        }
                                        showAmazonIcon={false}
                                        onProductClick={onProductClick}
                                        tabId={productDetailsTabToLinkTo}
                                    >
                                        {title}
                                    </ProductCatalogLink>
                                ) : (
                                    title
                                )}
                            </span>
                            <ResearchKeywordLink
                                countryCode={productMarketplace as CountryCode}
                                asin={productAsin}
                            />
                        </div>
                        {productAsin && productMarketplace && (
                            <div className={styles.linkContainer}>
                                {additionalLinkContent}
                                <AsinLink
                                    asin={productAsin}
                                    countryCode={
                                        productMarketplace as CountryCode
                                    }
                                    showAmazonIcon={false}
                                    color={
                                        linkToProductDetails
                                            ? 'muted'
                                            : 'default'
                                    }
                                    style={{ fontSize: 11 }}
                                />
                            </div>
                        )}
                    </Flex>
                </>
            )}
        </Flex>
    )
}

export default ProductTitle
